/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { z } from 'zod';

/**
 * One question a workflow asks.
 *
 * Its own module because two others need it and each needs the other: a
 * workflow says what it asks, and an agreement says what its own version
 * asked, so its values can be edited through the same questions the launch
 * form put. Defined in either of those, the two would import each other,
 * and whichever loaded second would find this not yet defined.
 */
/** Which dates a date question may be asked to take, in the order offered. */
export const DATE_RULES = ['any', 'today_or_earlier', 'today_or_later'] as const;

export const workflowFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  field_type: z.enum(['text', 'email', 'date', 'area', 'number', 'money']),
  is_required: z.boolean(),
  placeholder: z.string().nullable(),
  sort_order: z.number(),
  /**
   * What the answer means to the agreement: its effective date, its end
   * date or its value (`LC-11`). Null for an ordinary question, and from a
   * server that predates the question.
   */
  role: z.enum(['effective_date', 'end_date', 'value']).nullable().default(null),
  /**
   * How long an answer may be, by the kind of question: the server refuses a
   * longer one. A single line's 255 from a server that predates it.
   */
  max_length: z.number().int().positive().default(255),
  /**
   * Which dates a date question takes, besides any sane one: today or
   * earlier (a shipped date), today or later (a start date), or any. Any
   * for every other kind, and from a server that predates the choice.
   */
  date_rule: z.enum(DATE_RULES).default('any'),
});

export type WorkflowField = z.infer<typeof workflowFieldSchema>;

export type FieldRole = NonNullable<WorkflowField['role']>;

export type DateRule = WorkflowField['date_rule'];

/**
 * The roles a question of this kind may hold, by the server's own rule: a
 * date can be the effective or end date, money or a number the value.
 */
export function rolesFor(fieldType: WorkflowField['field_type']): FieldRole[] {
  if (fieldType === 'date') return ['effective_date', 'end_date'];
  if (fieldType === 'money' || fieldType === 'number') return ['value'];
  return [];
}
